import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import styles from "./Table.styles";

export default function Table({
  rows = [],
  columns = [],
  actionsRenderer,
  actionsHeader = null,
  pageSizeOptions = [100, 200, 400],
  initialRowsPerPage = 100,
  stickyHeader = true,
  zebra = true,
  hover = true,
  bordered = true,
  getRowId = (_row, i) => String(i),
  emptyMessage = "No records found",
  defaultSort = null,
  onSortChange,
  rowSx, 
}) {
  const safeRows = useMemo(() => Array.isArray(rows) ? rows : [], [rows]);

  const [sortField, setSortField] = useState(defaultSort?.field ?? null);
  const [sortDirection, setSortDirection] = useState(defaultSort?.direction ?? "asc");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const handleSortClick = (field, sortable) => {
    if (!sortable) return;
    if (sortField === field) {
      const next = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(next);
      onSortChange?.({ field, direction: next });
    } else {
      setSortField(field);
      setSortDirection("asc");
      onSortChange?.({ field, direction: "asc" });
    }
    setPage(0);
  };

  const getAccessor = (col) => {
    if (typeof col.accessor === "function") return col.accessor;
    if (col.type === "date") {
      return (row) => (row?.[col.field] ? new Date(row[col.field]).getTime() : 0);
    }
    if (col.type === "boolean") {
      return (row) => (row?.[col.field] ? 1 : 0);
    }
    if (col.type === "number") {
      return (row) => {
        const v = row?.[col.field];
        if (v === undefined || v === null || v === "") return null;
        const n = typeof v === "number" ? v : Number(v);
        return Number.isNaN(n) ? null : n;
      };
    }
    return (row) => {
      const v = row?.[col.field];
      return v == null ? "" : String(v);
    };
  };

  const sortedRows = useMemo(() => {
    if (!sortField) return safeRows;
    const col = columns.find((c) => c.field === sortField);
    if (!col) return safeRows;

    const acc = getAccessor(col);
    const dir = sortDirection === "asc" ? 1 : -1;

    return [...safeRows].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);

      if (col.type === "number" || col.type === "date" || col.type === "boolean") {
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [safeRows, sortField, sortDirection, columns]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  const tdBaseSx = {
    ...styles.tableCell,
    ...(bordered ? {} : { borderBottom: "none" }),
  };

  return (
    <Box sx={styles.tableWrapper}>
      <TableContainer
        component={Paper}
        sx={{
          ...styles.tableContainerWrapper,
          maxHeight: "none",
          overflow: "auto",
        }}
      >
        <MuiTable size="small" stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow sx={styles.tableHeader}>
              {columns.map((col) => {
                const active = sortField === col.field;
                return (
                  <TableCell
                    key={col.field}
                    sx={{ ...styles.tableHeaderCell, ...(col.headerSx || {}) }}
                    align={col.align || "left"}
                  >
                    <Box
                      onClick={() => handleSortClick(col.field, col.sortable)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        ...(col.sortable
                          ? {
                              cursor: "pointer",
                              "&:hover .sortIcon": { visibility: "visible" },
                            }
                          : {}),
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {col.label}
                      </Typography>
                      {col.sortable ? (
                        <Box
                          className="sortIcon"
                          sx={{
                            visibility: active ? "visible" : "hidden",
                            ml: 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {active ? (
                            sortDirection === "asc" ? (
                              <ArrowUpward fontSize="small" />
                            ) : (
                              <ArrowDownward fontSize="small" />
                            )
                          ) : (
                            <ArrowDownward fontSize="small" sx={{ opacity: 0.3 }} />
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  </TableCell>
                );
              })}
              {actionsRenderer && (
                <TableCell sx={styles.tableHeaderCell} align="left">
                  <Typography variant="body2" fontWeight="bold">
                    {actionsHeader}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow>
                <TableCell sx={tdBaseSx} colSpan={columns.length + (actionsRenderer ? 1 : 0)}>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, i) => {
                const rowIndex = i + page * rowsPerPage;
                const extraRowSx = typeof rowSx === "function" ? rowSx(row, rowIndex) : undefined;

                return (
                  <TableRow
                    key={getRowId(row, rowIndex)}
                    sx={{
                      ...styles.tableRow,
                      ...(zebra
                        ? {
                            "&:nth-of-type(odd)": { backgroundColor: "#fff" },
                            "&:nth-of-type(even)": { backgroundColor: "#f0f0f0" },
                          }
                        : {}),
                      ...(extraRowSx || {}),
                    }}
                    hover={hover}
                  >
                    {columns.map((col) => {
                      const acc = getAccessor(col);
                      const rawValue = acc(row);
                      const content =
                        typeof col.render === "function"
                          ? col.render(row, rawValue)
                          : defaultRender(col, rawValue, row);
                      return (
                        <TableCell
                          key={col.field}
                          sx={{ ...tdBaseSx, ...(col.cellSx || {}) }}
                          align={col.align || "left"}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                    {actionsRenderer && (
                      <TableCell sx={tdBaseSx}>
                        {actionsRenderer(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      <Box sx={styles.paginationContainer}>
        <TablePagination
          component="div"
          count={safeRows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={pageSizeOptions}
          sx={styles.tablePagination}
        />
      </Box>
    </Box>
  );
}

function defaultRender(col, value, row) {
  switch (col.type) {
    case "boolean":
      return (
        <Checkbox
          checked={!!value}
          disabled
          sx={{
            p: 0,
            "&.Mui-disabled": { color: "#9e9e9e" },
            "&.Mui-checked.Mui-disabled": { color: "#9e9e9e" },
          }}
          inputProps={{ "aria-label": `${col.label}` }}
        />
      );
    case "date":
      return value ? new Date(value).toLocaleDateString("en-GB") : "";
    case "number":
      return value === null || value === undefined ? "" : String(value);
    default:
      return value == null ? "" : String(value);
  }
}
